import Box from "@/common/components/Box";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { AxiosError } from "axios";
import { AlertCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import EquipmentDataType from "./components/EquipmentData";
import TextArea from "@/common/components/TextArea";
import { UnitTextField } from "@/common/components/UnitTextField";
import Alert from "@/common/components/Alert";
import MaintenanceWorkflowSelectSolicitations from "./components/MaintenanceWorkflowSelectSolicitations";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@/common/components/Button";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useQueryClient } from "@tanstack/react-query";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import useEquipment from "@/features/equipments/hooks/useEquipment";
import { maintenanceWorkflowSchema, MaintenanceWorkflowValidationSchema } from "../../schemas/maintenanceWorkflow.schema";
import { useCreateMaintenanceWorkflow } from "./hooks/useCreateMaintenanceWorkflow";
import { unities } from "@/common/constants/unities.constants";

const MaintenanceWorkflowsCreate = () => {
  const queryClient = useQueryClient();

  const goBack = useGoBack();

  const { filterParams } = useFilterParams();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    data: equipment,
    isError: equipmentIsError,
    error: equipmentError,
  } = useEquipment({
    id: filterParams.equipment,
    options: {
      retry: false,
    },
  });

  const formMethods = useForm<MaintenanceWorkflowValidationSchema>({
    resolver: zodResolver(maintenanceWorkflowSchema),
    defaultValues: {
      solicitation_ids: [],
    }
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: {
      errors,
    },
  } = formMethods;

  const {
    mutate: createMaintenanceWorkflow,
    isPending: createMaintenanceWorkflowIsPending,
  } = useCreateMaintenanceWorkflow({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflows'] });

      goBack({
        fallback: `/maintenance-workflows`,
        searchLastRouteFromFallback: true,
      });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
      })(error);
    },
  });

  const onSubmit: SubmitHandler<MaintenanceWorkflowValidationSchema> = (data) => createMaintenanceWorkflow(data);

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const hasPermission = hasUserPermission('Client::Maintenance::Workflows::Create');

  useEffect(() => {
    if (!filterParams.equipment || equipmentError?.code === AxiosError.ERR_BAD_REQUEST) {
      goBack({
        fallback: `/maintenance-workflows`,
        searchLastRouteFromFallback: true,
      });
    }
  }, [equipmentIsError]);

  return (
    <FormProvider {...formMethods}>
      <div>
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/maintenance-workflows`,
          }}
          title="Processos de manutenção (OS)"
          subtitle="Criação rápida"
          titleClassName="text-md"
          className="text-white-400"
          subtitleClassName="text-nowrap font-medium"
          buttons={hasPermission && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline-error"
                disabled={createMaintenanceWorkflowIsPending}
                onClick={() => {
                  goBack({
                    fallback: `/maintenance-workflows`,
                    searchLastRouteFromFallback: true,
                  });
                }}
              >
                Cancelar
              </Button>

              <Button
                onClick={handleSubmit(onSubmit)}
                isLoading={createMaintenanceWorkflowIsPending}
              >
                Gerar processo
              </Button>
            </div>
          )}
        />

        <ErrorMessageWrapper
          statusCode={isUserPermissionsPending || hasPermission ? 200 : 403}
          className="h-[calc(100vh-8rem)]"
        >
          <Box className="flex flex-col gap-4 max-h-[calc(100vh-8rem)] overflow-auto">
            <EquipmentDataType
              equipment={equipment}
            />

            <div className="flex gap-4">
              <TextArea
                label="Descrição geral do processo"
                markAsRequired
                className="flex-1"
                textareaClassName="text-xs"
                rows={2}
                {...register('observations')}
                errorMessage={errors.observations?.message}
              />

              <UnitTextField
                label="Odômetro/Horímetro atual do equipamento"
                markAsRequired
                decimals={0}
                unit={equipment?.model?.category?.unit_for_consumption && unities[equipment?.model?.category?.unit_for_consumption]}
                labelClass="h-[86px]"
                valueClassName="h-full flex-1"
                {...register('current_equipment_usage')}
                errorMessage={errors.current_equipment_usage?.message}
              />
            </div>

            <Alert
              variant="warning"
              Icon={AlertCircleIcon}
              containerClassName="bg-transparent p-0"
            >
              <div className="flex flex-col">
                <p className="text-xs">
                  Você só poderá gerar um processo de manutenção com ao menos 1 solicitação selecionada.
                </p>

                <p className="text-xs italic font-light">
                  Selecione os programas que você deseja adiantar e as solicitações que deseja unir em um processo de manutenção
                </p>
              </div>
            </Alert>

            {errors.solicitation_ids?.message && (
              <ErrorMessage
                name="solicitation_ids"
                message={errors.solicitation_ids?.message}
              />
            )}

            <MaintenanceWorkflowSelectSolicitations />
          </Box>
        </ErrorMessageWrapper>
      </div>

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </FormProvider>
  );
}

export default MaintenanceWorkflowsCreate;
